import { HeroImage, HeroImageLocation } from 'components/HeroConsolidated/shared/HeroImage';
import HeroContent from 'components/HeroConsolidated/shared/HeroContent';
import HeroCTA from 'components/HeroConsolidated/shared/HeroCTA';
import BaseWithBlogAuthor from './shared/BaseWithBlogAuthor';
import BlogCategories from './shared/BlogCategories';
import HeroWrapper, { HeroContentWrapper } from 'components/HeroConsolidated/shared/HeroWrapper';
import { VideoControls, VideoPlayer, VideoPreview } from 'components/VideoPlayer';
import { HeroConsolidatedProps } from './types';
import { cn } from 'utils/styles';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { NotificationOverlayDesktopPosition } from 'types/constants';
import { formatDate } from 'utils/formatters';

const HeroBaseWithBlogVariant = ({
  mobileMediaLocation,
  sys,
  author,
  blogDate,
  headlineSize,
  color,
  headline,
  copy,
  showEmailInput,
  emailInputButtonText,
  cta,
  disclaimerModal,
  scrollCta,
  disclaimers,
  margins,
  sectionName,
  imageOverlay,
  notificationOverlayDesktopPosition,
  desktopMedia,
  desktopAspectRatio,
  mobileMedia,
  mobileAspectRatio,
  allowPromoCodeEntry,
  promoCodeInputButtonText,
  promoCodes,
  video,
  copySize,
  className,
  innerClassName,
  headingLevel,
  enrollmentUrl,
  slideIndex,
  isSlider,
  isInactiveCarouselSlide,
  hyperlinkedMediaCollection,
  concepts,
}: HeroConsolidatedProps) => {
  const hasVideo = !!video;
  const hasImageOnly = !!desktopMedia && !hasVideo;
  const hasMedia = hasVideo || !!desktopMedia;
  const defaultMobileMediaPlacement = mobileMediaLocation === HeroImageLocation.Default || !mobileMediaLocation;
  const placeMobileImageUnderCta = mobileMediaLocation === HeroImageLocation.BetweenCtaAndDisclaimer;
  const notificationOverlay = imageOverlay ? { image: imageOverlay, display: true } : undefined;
  const formattedDate = blogDate ? formatDate(blogDate) : null;

  // Fallback support for modules without notificationOverlayDesktopPosition selection
  if (!notificationOverlayDesktopPosition) {
    notificationOverlayDesktopPosition = NotificationOverlayDesktopPosition.TopLeft;
  }

  const videoPlayer = hasVideo && (
    <div className={cn('max-h-[490px] w-auto')}>
      <VideoPlayer video={video} autoPlay rounded>
        <VideoPreview image={video?.previewImage} showPreviewOnEnd={false} />
        <VideoControls position="center" />
      </VideoPlayer>
    </div>
  );

  const media = desktopMedia && (
    <HeroImage
      media={desktopMedia}
      aspectRatio={desktopAspectRatio}
      mobileMedia={mobileMedia}
      mobileAspectRatio={mobileAspectRatio}
      notificationOverlay={notificationOverlay}
      notificationOverlayDesktopPosition={notificationOverlayDesktopPosition}
      isSlider={isSlider}
      priority={slideIndex === 0}
    />
  );

  return (
    <HeroWrapper
      testId={'baseVariant'}
      color={color}
      margins={margins}
      sectionName={sectionName}
      className={className}
      innerClassName={innerClassName}
      isInactiveCarouselSlide={isInactiveCarouselSlide}>
      <HeroContentWrapper className={cn('justify-between', innerClassName)}>
        <div>
          <div className="flex flex-col-reverse">
            <HeroContent
              headline={headline}
              copy={copy}
              sys={sys}
              headlineSize={headlineSize}
              copySize={copySize}
              headingLevel={headingLevel}
            />
            {formattedDate && (
              <div className="text-eyebrow font-sans mb-5" {...liveUpdateAttrs(sys?.id, 'blogDate')} data-testid="date">
                {formattedDate}
              </div>
            )}
          </div>
          {hasMedia && defaultMobileMediaPlacement && (
            <div className="sm:hidden mt-8">
              {hasImageOnly && (
                <div className="hero-image-container relative mb-8" {...liveUpdateAttrs(sys?.id, 'desktopMedia')}>
                  {media}
                </div>
              )}
              {videoPlayer}
            </div>
          )}
          {author && <BaseWithBlogAuthor {...author} />}
          <HeroCTA
            showEmailInput={showEmailInput}
            sys={sys}
            emailInputButtonText={emailInputButtonText}
            cta={cta}
            disclaimerModal={disclaimerModal}
            scrollCta={scrollCta}
            disclaimers={disclaimers}
            allowPromoCodeEntry={allowPromoCodeEntry}
            promoCodeInputButtonText={promoCodeInputButtonText}
            promoCodes={promoCodes}
            imageOverlay={imageOverlay}
            defaultMobileMediaPlacement={defaultMobileMediaPlacement}
            enrollmentUrl={enrollmentUrl}
            placeMobileImageUnderCta={placeMobileImageUnderCta}
            image={media}
            hyperlinkedMedia={hyperlinkedMediaCollection?.items}
          />
        </div>
        {concepts && concepts.length > 0 && <BlogCategories concepts={concepts} />}
      </HeroContentWrapper>

      {hasMedia && (
        <div
          className={cn(
            'hero-media-container sm:basis-1/2 sm:w-1/2 h-full w-full grow-0',
            (defaultMobileMediaPlacement || placeMobileImageUnderCta) && 'max-sm:hidden',
          )}
          data-testid="hero-media-container">
          {hasImageOnly && (
            <div
              className={cn(`hero-image-container hero-right relative flex`)}
              {...liveUpdateAttrs(sys?.id, 'desktopMedia')}>
              {media}
            </div>
          )}

          {videoPlayer}
        </div>
      )}
    </HeroWrapper>
  );
};

export default HeroBaseWithBlogVariant;
